import {
    type DraftedMessageButtonWidgetProps,
    type EstimatedBill,
    type EstimatedBillsWidgetProps,
    type SavingsGoalAmountsWidgetProps,
    type SavingsGoalEstimate,
    WidgetType,
} from '@fruitful/types';

import type { ChatMessage } from '../types/ChatTypes';

export interface ActiveDraftedMessageButtonWidget {
    messageId: string;
    contentIndex: number;
    props: DraftedMessageButtonWidgetProps;
}

const isDraftedMessageButtonWidgetProps = (
    props: Record<string, unknown> | null,
): props is DraftedMessageButtonWidgetProps & Record<string, unknown> => {
    if (props == null) {
        return false;
    }

    return (
        typeof props.label === 'string' &&
        typeof props.confirmationPrefix === 'string' &&
        typeof props.confirmationPayload === 'object' &&
        props.confirmationPayload != null
    );
};

const isEstimatedBillsWidgetProps = (
    props: Record<string, unknown> | null,
): props is EstimatedBillsWidgetProps & Record<string, unknown> => {
    if (props == null) {
        return false;
    }

    return (
        Array.isArray(props.bills) &&
        typeof props.confirmationPrefix === 'string' &&
        typeof props.confirmationPayload === 'object' &&
        props.confirmationPayload != null
    );
};

export const getActiveDraftedMessageButtonWidget = (
    messages: ChatMessage[],
): ActiveDraftedMessageButtonWidget | null => {
    for (
        let messageIndex = messages.length - 1;
        messageIndex >= 0;
        messageIndex -= 1
    ) {
        const message = messages[messageIndex];
        if (message?.role !== 'assistant') {
            continue;
        }

        const hasUserReplyAfterMessage = messages
            .slice(messageIndex + 1)
            .some((reply) => reply.role === 'user');

        if (hasUserReplyAfterMessage) {
            return null;
        }

        for (
            let contentIndex = message.content.length - 1;
            contentIndex >= 0;
            contentIndex -= 1
        ) {
            const content = message.content[contentIndex];
            if (content?.type !== 'ui') {
                continue;
            }

            if (
                content.widget === WidgetType.DraftedMessageButton &&
                isDraftedMessageButtonWidgetProps(content.props)
            ) {
                return {
                    messageId: message.id,
                    contentIndex,
                    props: content.props,
                };
            }

            if (
                content.widget === WidgetType.EstimatedBills &&
                isEstimatedBillsWidgetProps(content.props)
            ) {
                return {
                    messageId: message.id,
                    contentIndex,
                    props: {
                        label: 'Looks good',
                        confirmationPrefix: content.props.confirmationPrefix,
                        confirmationPayload: content.props.confirmationPayload,
                    },
                };
            }
        }
    }

    return null;
};

export interface ActiveEstimatedBillsWidget {
    messageId: string;
    contentIndex: number;
    props: EstimatedBillsWidgetProps;
    confirmed: boolean;
    /** Bills from the [bills-confirmed] user message (reflects user edits). */
    confirmedBills: EstimatedBill[] | null;
}

export const getActiveEstimatedBillsWidget = (
    messages: ChatMessage[],
): ActiveEstimatedBillsWidget | null => {
    for (
        let messageIndex = messages.length - 1;
        messageIndex >= 0;
        messageIndex -= 1
    ) {
        const message = messages[messageIndex];
        if (message?.role !== 'assistant') {
            continue;
        }

        for (
            let contentIndex = message.content.length - 1;
            contentIndex >= 0;
            contentIndex -= 1
        ) {
            const content = message.content[contentIndex];
            if (content?.type !== 'ui') {
                continue;
            }

            if (
                content.widget === WidgetType.EstimatedBills &&
                isEstimatedBillsWidgetProps(content.props)
            ) {
                let confirmed = false;
                let confirmedBills: EstimatedBill[] | null = null;

                const laterMessages = messages.slice(messageIndex + 1);
                for (const m of laterMessages) {
                    if (m.role !== 'user') continue;
                    for (const c of m.content) {
                        if (c.type !== 'text') continue;
                        const prefix = '[bills-confirmed]';
                        const idx = c.text.indexOf(prefix);
                        if (idx === -1) continue;
                        confirmed = true;
                        try {
                            const parsed = JSON.parse(
                                c.text.slice(idx + prefix.length).trim(),
                            ) as { bills?: EstimatedBill[] };
                            if (Array.isArray(parsed.bills)) {
                                confirmedBills = parsed.bills;
                            }
                        } catch {
                            // payload missing or malformed — fall back to widget props
                        }
                    }
                    if (confirmed) break;
                }

                // The bills widget is a pending footer interaction. If the
                // member sent any message after it appeared — e.g. a free-text
                // override like "my bills are $4k/mo" instead of clicking
                // "Looks good" — the conversation has moved on. Treat the
                // widget as resolved so it stops re-appearing in the footer.
                if (
                    !confirmed &&
                    laterMessages.some((m) => m.role === 'user')
                ) {
                    confirmed = true;
                }

                return {
                    messageId: message.id,
                    contentIndex,
                    props: content.props,
                    confirmed,
                    confirmedBills,
                };
            }
        }
    }

    return null;
};

const isSavingsGoalAmountsWidgetProps = (
    props: Record<string, unknown> | null,
): props is SavingsGoalAmountsWidgetProps & Record<string, unknown> => {
    if (props == null) {
        return false;
    }

    return (
        Array.isArray(props.goals) &&
        typeof props.confirmationPrefix === 'string' &&
        typeof props.confirmationPayload === 'object' &&
        props.confirmationPayload != null
    );
};

export interface ActiveSavingsGoalAmountsWidget {
    messageId: string;
    contentIndex: number;
    props: SavingsGoalAmountsWidgetProps;
    confirmed: boolean;
    confirmedGoals: SavingsGoalEstimate[] | null;
}

export const getActiveSavingsGoalAmountsWidget = (
    messages: ChatMessage[],
): ActiveSavingsGoalAmountsWidget | null => {
    for (
        let messageIndex = messages.length - 1;
        messageIndex >= 0;
        messageIndex -= 1
    ) {
        const message = messages[messageIndex];
        if (message?.role !== 'assistant') {
            continue;
        }

        for (
            let contentIndex = message.content.length - 1;
            contentIndex >= 0;
            contentIndex -= 1
        ) {
            const content = message.content[contentIndex];
            if (content?.type !== 'ui') {
                continue;
            }

            if (
                content.widget === WidgetType.SavingsGoalAmounts &&
                isSavingsGoalAmountsWidgetProps(content.props)
            ) {
                let confirmed = false;
                let confirmedGoals: SavingsGoalEstimate[] | null = null;

                for (const m of messages.slice(messageIndex + 1)) {
                    if (m.role !== 'user') continue;
                    for (const c of m.content) {
                        if (c.type !== 'text') continue;
                        const prefix = '[savings-goals-confirmed]';
                        const idx = c.text.indexOf(prefix);
                        if (idx === -1) continue;
                        confirmed = true;
                        try {
                            const parsed = JSON.parse(
                                c.text.slice(idx + prefix.length).trim(),
                            ) as { goals?: SavingsGoalEstimate[] };
                            if (Array.isArray(parsed.goals)) {
                                confirmedGoals = parsed.goals;
                            }
                        } catch {
                            // payload missing or malformed
                        }
                    }
                    if (confirmed) break;
                }

                return {
                    messageId: message.id,
                    contentIndex,
                    props: content.props,
                    confirmed,
                    confirmedGoals,
                };
            }
        }
    }

    return null;
};

export interface IncomeInputWidgetProps {
    isJoint: boolean;
}

export interface ActiveIncomeInputWidget {
    messageId: string;
    contentIndex: number;
    props: IncomeInputWidgetProps;
    answered: boolean;
}

const isIncomeInputWidgetProps = (
    props: Record<string, unknown> | null,
): props is IncomeInputWidgetProps & Record<string, unknown> => {
    if (props == null) {
        return false;
    }
    return typeof props.isJoint === 'boolean';
};

export const getActiveIncomeInputWidget = (
    messages: ChatMessage[],
): ActiveIncomeInputWidget | null => {
    for (
        let messageIndex = messages.length - 1;
        messageIndex >= 0;
        messageIndex -= 1
    ) {
        const message = messages[messageIndex];
        if (message?.role !== 'assistant') {
            continue;
        }

        for (
            let contentIndex = message.content.length - 1;
            contentIndex >= 0;
            contentIndex -= 1
        ) {
            const content = message.content[contentIndex];
            if (content?.type !== 'ui') {
                continue;
            }

            if (
                content.widget === WidgetType.IncomeInput &&
                isIncomeInputWidgetProps(content.props)
            ) {
                const answered = messages
                    .slice(messageIndex + 1)
                    .some((m) => m.role === 'user');

                return {
                    messageId: message.id,
                    contentIndex,
                    props: content.props,
                    answered,
                };
            }
        }
    }

    return null;
};
